import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Send, CheckCircle2, Building2 } from 'lucide-react';
import axios from 'axios';
import { trackFormSubmit, trackCTAClick } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RequestDemo = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    company: '',
    company_size: '',
    role: '',
    workflow: '',
    message: '',
  });

  const workflows = [
    'PR Governance',
    'Security & Compliance',
    'Customer Support',
    'Sales Deal Desk',
    'Vendor Onboarding',
    'Other',
  ];

  const companySizes = [
    '1-50',
    '51-200',
    '201-500',
    '501-1000',
    '1000+',
  ];

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    trackCTAClick('demo_form_submit', 'request-demo-page');

    try {
      await axios.post(`${BACKEND_URL}/api/leads/demo`, {
        full_name: formData.full_name,
        email: formData.email,
        company: formData.company,
        company_size: formData.company_size,
        role: formData.role,
        phone: null,
        message: formData.workflow ? `Workflow Interest: ${formData.workflow}. ${formData.message}` : formData.message,
      });

      setSubmitted(true);
      trackFormSubmit('demo_form', true, { workflow: formData.workflow, company_size: formData.company_size });
      toast.success('Demo request submitted successfully!');
    } catch (error) {
      console.error('Error submitting demo request:', error);
      trackFormSubmit('demo_form', false);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F6F7FB] pt-32 pb-20">
        <Helmet>
          <title>Request Submitted | Liquicent</title>
        </Helmet>
        <div className="max-w-xl mx-auto px-6 text-center">
          <div className="w-20 h-20 rounded-full bg-[#2CB1A1]/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-[#2CB1A1]" />
          </div>
          <h1 className="text-3xl font-bold text-[#0F172A] mb-4">Thank You!</h1>
          <p className="text-lg text-[#475569] mb-8">
            Your demo request has been submitted. Our team will reach out within 24 hours to schedule your personalized demo.
          </p>
          <Button 
            onClick={() => navigate('/')}
            variant="outline"
            className="border-[#1A3A8F] text-[#1A3A8F] hover:bg-[#1A3A8F] hover:text-white"
          >
            Back to Homepage
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F7FB]">
      <Helmet>
        <title>Request Demo | Liquicent</title>
        <meta name="description" content="Request a personalized demo of Liquicent's coordination infrastructure platform." />
      </Helmet>

      <div className="pt-32 pb-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Left Column - Info */}
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold text-[#0F172A] mb-6">
                Request a Demo
              </h1>
              <p className="text-lg text-[#475569] mb-10 leading-relaxed">
                See how Liquicent can transform your enterprise workflows with AI-powered coordination infrastructure.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#1A3A8F]/10 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5 text-[#1A3A8F]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0F172A] mb-1">Personalized Demo</h3>
                    <p className="text-sm text-[#475569]">
                      See Liquicent in action with workflows relevant to your organization.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#2CB1A1]/10 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-5 h-5 text-[#2CB1A1]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0F172A] mb-1">No Commitment</h3>
                    <p className="text-sm text-[#475569]">
                      Free consultation with our solution architects.
                    </p>
                  </div>
                </div>
              </div>

              {/* Trust Logos Placeholder */}
              <div className="mt-12 pt-8 border-t border-[#E5E7EB]">
                <p className="text-sm text-[#64748B] mb-4">Trusted by leading enterprises</p>
                <div className="flex items-center gap-8 opacity-50">
                  {/* Placeholder for company logos */}
                  <div className="h-8 w-24 bg-[#E5E7EB] rounded" />
                  <div className="h-8 w-20 bg-[#E5E7EB] rounded" />
                  <div className="h-8 w-28 bg-[#E5E7EB] rounded" />
                </div>
              </div>
            </div>

            {/* Right Column - Form */}
            <div>
              <Card className="p-8 bg-white border border-[#E5E7EB] rounded-2xl shadow-sm">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="full_name" className="text-[#0F172A]">Full Name *</Label>
                      <Input
                        id="full_name"
                        required
                        value={formData.full_name}
                        onChange={(e) => handleChange('full_name', e.target.value)}
                        placeholder="John Doe"
                        className="bg-[#F6F7FB] border-[#E5E7EB] focus:border-[#1A3A8F]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-[#0F172A]">Work Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        placeholder="john@company.com"
                        className="bg-[#F6F7FB] border-[#E5E7EB] focus:border-[#1A3A8F]"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="company" className="text-[#0F172A]">Company *</Label>
                      <Input
                        id="company"
                        required
                        value={formData.company}
                        onChange={(e) => handleChange('company', e.target.value)}
                        placeholder="Acme Inc."
                        className="bg-[#F6F7FB] border-[#E5E7EB] focus:border-[#1A3A8F]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company_size" className="text-[#0F172A]">Company Size *</Label>
                      <Select value={formData.company_size} onValueChange={(v) => handleChange('company_size', v)}>
                        <SelectTrigger className="bg-[#F6F7FB] border-[#E5E7EB]">
                          <SelectValue placeholder="Select size" />
                        </SelectTrigger>
                        <SelectContent>
                          {companySizes.map((size) => (
                            <SelectItem key={size} value={size}>{size} employees</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="role" className="text-[#0F172A]">Your Role *</Label>
                      <Input
                        id="role"
                        required
                        value={formData.role}
                        onChange={(e) => handleChange('role', e.target.value)}
                        placeholder="VP of Engineering"
                        className="bg-[#F6F7FB] border-[#E5E7EB] focus:border-[#1A3A8F]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="workflow" className="text-[#0F172A]">Workflow Interest</Label>
                      <Select value={formData.workflow} onValueChange={(v) => handleChange('workflow', v)}>
                        <SelectTrigger className="bg-[#F6F7FB] border-[#E5E7EB]">
                          <SelectValue placeholder="Select workflow" />
                        </SelectTrigger>
                        <SelectContent>
                          {workflows.map((wf) => (
                            <SelectItem key={wf} value={wf}>{wf}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-[#0F172A]">Tell us about your needs (optional)</Label>
                    <Textarea
                      id="message"
                      rows={4}
                      value={formData.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                      placeholder="What challenges are you looking to solve?"
                      className="bg-[#F6F7FB] border-[#E5E7EB] focus:border-[#1A3A8F] resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#1A3A8F] hover:bg-[#142D70] text-white py-6 text-base font-medium"
                  >
                    {loading ? (
                      'Submitting...'
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Request Demo
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-[#64748B] text-center">
                    By submitting, you agree to our{' '}
                    <a href="/privacy" className="text-[#1A3A8F] hover:underline">Privacy Policy</a>
                    {' '}and{' '}
                    <a href="/terms" className="text-[#1A3A8F] hover:underline">Terms of Service</a>.
                  </p>
                </form>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestDemo;
